import React from 'react';
import {
    Box,
    Button,
    Typography,
    Divider,
    Paper,
    CircularProgress,
    Fade
} from '@mui/material';

type InvitationConfirmationProps = {
    open: boolean;
    onContinue: () => void;
    onCancel: () => void;
    isLoading?: boolean;
    isValid?: boolean;
    invitationData: {
        email?: string;
        companyName?: string;
        role?: string;
    };
};

const InvitationConfirmation: React.FC<InvitationConfirmationProps> = ({
    open,
    onContinue,
    onCancel,
    isLoading = false,
    isValid,
    invitationData
}) => {
    const [displayData, setDisplayData] = React.useState(invitationData);

    React.useEffect(() => {
        if (invitationData) {
            setDisplayData(invitationData);
        }
    }, [invitationData]);

    if (!open) return null;

    return (
        <Fade in={open} timeout={300}>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'linear-gradient(135deg, #00B89F 0%, #009688 50%, #00695C 100%)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Paper
                    sx={{
                        maxWidth: 480,
                        width: '100%',
                        borderRadius: 4,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {isLoading ? (
                        <Box
                            sx={{
                                p: 6,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3
                            }}
                        >
                            <CircularProgress 
                                size={48} 
                                sx={{ color: 'var(--color-primary)' }} 
                            />
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 500,
                                    color: 'var(--color-default-text)',
                                    textAlign: 'center'
                                }}
                            >
                                Checking invitation...
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    maxWidth: 300
                                }}
                            >
                                Please wait while we verify your invitation details.
                            </Typography>
                        </Box>
                    ) : isValid === false ? (
                        <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 48 }}>🔒</Typography>
                            <Typography sx={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-default-text)' }}>
                                Invitation Invalid
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, maxWidth: 340 }}>
                                This invitation is no longer valid. Please request a new invitation.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={onCancel}
                                fullWidth
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: 'var(--color-primary)',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: '#009688' }
                                }}
                            >
                                Close
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography
                                    sx={{ 
                                        fontSize: '28px',
                                        fontWeight: 700,
                                        color: 'var(--color-default-text)',
                                        mb: 1
                                    }}
                                >
                                    🎉 You’ve been invited to access <strong>{displayData?.companyName}</strong>
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        fontSize: '16px', 
                                        lineHeight: 1.6, 
                                        color: 'text.secondary',
                                        mb: 3
                                    }}
                                >
                                    You have been granted access to this company account. 
                                    Please confirm to continue with your registration.
                                </Typography>

                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        backgroundColor: '#f8f9fa', 
                                        borderRadius: 3, 
                                        p: 3,
                                        border: '1px solid #e9ecef',
                                        textAlign: 'left'
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)' }}>
                                                Email:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-default-text)' }}>
                                                {displayData?.email || 'N/A'}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 0.5 }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)' }}>
                                                Company:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-default-text)' }}>
                                                {displayData?.companyName || 'N/A'}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 0.5 }} />

                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)' }}>
                                                Role:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--color-default-text)' }}>
                                                {displayData?.role || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            <Box 
                                sx={{ 
                                    p: 3, 
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={onContinue}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'var(--color-primary)',
                                        color: 'white',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#009688',
                                            boxShadow: '0 4px 12px rgba(0, 184, 159, 0.3)'
                                        }
                                    }}
                                >
                                    Continue
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        borderColor: '#ddd',
                                        color: 'var(--color-text-muted)',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        '&:hover': {
                                            borderColor: 'var(--color-text-muted)',
                                            backgroundColor: 'var(--color-container-background)'
                                        }
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </Fade>
    );
};

export default InvitationConfirmation;